import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Check,
  Clock,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  Sparkles,
  Star,
  Trash2,
} from 'lucide-react';
import type { Resource } from '@itr/shared';
import { getLocale, formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  bulkResultMessage,
  runBulk,
  useDebounced,
  useFetch,
  useSelection,
  useToast,
  ReachDot,
} from '../components/ui';

interface VaultEntry {
  id: string;
  name: string;
  type: string;
  username: string | null;
  url: string | null;
  tags: string[];
  resourceId: string | null;
  strength: number | null;
  compromised: boolean;
  favorite: boolean;
  hasSecret: boolean;
  hasTotp: boolean;
  hasNotes: boolean;
  rotationDays: number | null;
  rotationDueInDays: number | null;
  expiresAt: number | null;
  lastRotatedAt: number | null;
  updatedAt: number;
}

const STRENGTH_LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Excellent'];

export function VaultPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const debounced = useDebounced(search, 250);

  const { data, loading, error, reload } = useFetch(
    () => api.get<VaultEntry[]>('/api/vault/entries', { search: debounced || undefined, type: type || undefined, limit: 300 }),
    [debounced, type],
  );
  const { data: audit } = useFetch(
    () => api.get<{ summary: Record<string, number> }>('/api/vault/audit'),
    [],
  );

  const entries = data ?? [];
  const canWrite = can('vault:write');
  const selection = useSelection(entries.map((e) => e.id));

  const bulkDelete = async () => {
    if (!confirm(`Delete ${selection.count} credential(s)? This cannot be undone.`)) return;
    const result = await runBulk(selection.selectedIds, (id) => api.delete(`/api/vault/entries/${id}`));
    const { message, intent } = bulkResultMessage('Deleted', result);
    toast(message, intent);
    selection.clear();
    reload();
  };

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Password safe</h1>
          <div className="page-subtitle">
            {entries.length} credential{entries.length === 1 ? '' : 's'} · encrypted at rest, every reveal audited
          </div>
        </div>
        {can('vault:write') && (
          <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus size={15} /> Add credential
          </button>
        )}
      </div>

      {audit?.summary && (
        <div className="stat-strip">
          <Chip label="Stored" value={audit.summary.total} />
          <Chip label="Weak" value={audit.summary.weak} intent={audit.summary.weak > 0 ? 'warn' : undefined} />
          <Chip label="Reused" value={audit.summary.reused} intent={audit.summary.reused > 0 ? 'warn' : undefined} />
          <Chip label="Rotation overdue" value={audit.summary.overdue} intent={audit.summary.overdue > 0 ? 'bad' : undefined} />
          <Chip label="Expiring soon" value={audit.summary.expiring} intent={audit.summary.expiring > 0 ? 'warn' : undefined} />
        </div>
      )}

      <div className="row" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', translate: '0 -50%', color: 'var(--itr-text-subtle)' }} />
          <input
            className="input"
            style={{ paddingLeft: 32 }}
            placeholder="Search credentials…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="row" style={{ gap: 4 }}>
          {['', 'password', 'ssh_key', 'api_key', 'certificate', 'wifi'].map((value) => (
            <button key={value || 'all'} className={`btn btn-sm${type === value ? ' btn-primary' : ''}`} onClick={() => setType(value)}>
              {value ? value.replace('_', ' ') : 'All'}
            </button>
          ))}
        </div>
      </div>

      {canWrite && (
        <SelectionBar selection={selection}>
          <button className="btn btn-sm btn-danger" onClick={bulkDelete}>
            <Trash2 size={12} /> Delete
          </button>
        </SelectionBar>
      )}

      <div className="card">
        {loading && !data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={7} cols={4} />
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : entries.length === 0 ? (
          <EmptyState
            icon={<KeyRound size={20} />}
            title={search ? 'No matching credentials' : 'The safe is empty'}
            text={
              search
                ? 'Try a different search.'
                : 'Store device passwords, SSH keys and API tokens here, linked to the resources they belong to.'
            }
            action={
              can('vault:write') && !search ? (
                <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
                  <Plus size={15} /> Add your first credential
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canWrite && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={selection} />
                    </th>
                  )}
                  <th style={{ width: 34 }} />
                  <th>Name</th>
                  <th style={{ width: 160 }}>Username</th>
                  <th style={{ width: 110 }}>Type</th>
                  <th style={{ width: 130 }}>Strength</th>
                  <th style={{ width: 130 }}>Rotation</th>
                  <th data-align="right" style={{ width: 100 }}>
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} data-clickable="true" onClick={() => navigate(`/vault/${entry.id}`)}>
                    {canWrite && (
                      <td>
                        <RowCheckbox selection={selection} id={entry.id} />
                      </td>
                    )}
                    <td>{entry.favorite && <Star size={13} style={{ fill: 'var(--itr-warning)', color: 'var(--itr-warning)' }} />}</td>
                    <td>
                      <div className="row" style={{ gap: 7 }}>
                        <span style={{ fontWeight: 550 }}>{entry.name}</span>
                        {entry.compromised && (
                          <Badge intent="bad">
                            <ShieldAlert size={10} /> compromised
                          </Badge>
                        )}
                      </div>
                      {entry.url && (
                        <div className="subtle truncate" style={{ fontSize: 11.5, maxWidth: 300 }}>
                          {entry.url}
                        </div>
                      )}
                    </td>
                    <td className="mono subtle">{entry.username ?? '—'}</td>
                    <td>
                      <Badge>{entry.type.replace('_', ' ')}</Badge>
                    </td>
                    <td>
                      <StrengthMeter strength={entry.strength} />
                    </td>
                    <td>
                      <RotationCell entry={entry} />
                    </td>
                    <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
                      {formatRelative(entry.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Add a credential">
        <CredentialForm
          onSaved={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      </Drawer>
    </>
  );
}

function Chip({ label, value, intent }: { label: string; value: number | undefined; intent?: 'warn' | 'bad' }) {
  return (
    <div className="stat-chip">
      <div className="stat-chip-label">{label}</div>
      <div
        className="stat-chip-value"
        style={{ color: intent === 'bad' ? 'var(--itr-critical)' : intent === 'warn' ? 'var(--itr-warning)' : undefined }}
      >
        {value ?? 0}
      </div>
    </div>
  );
}

function StrengthMeter({ strength }: { strength: number | null }) {
  if (strength === null) return <span className="subtle">—</span>;
  const color = strength <= 1 ? 'var(--itr-critical)' : strength === 2 ? 'var(--itr-warning)' : 'var(--itr-success)';

  return (
    <div className="row" style={{ gap: 7 }} title={STRENGTH_LABELS[strength]}>
      <span className="row" style={{ gap: 2 }}>
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            style={{
              width: 13,
              height: 4,
              borderRadius: 2,
              background: i < strength ? color : 'var(--itr-surface-sunken)',
            }}
          />
        ))}
      </span>
      {/* The bars alone would be colour-only encoding, so the word ships too. */}
      <span className="subtle" style={{ fontSize: 11.5 }}>
        {STRENGTH_LABELS[strength]}
      </span>
    </div>
  );
}

function RotationCell({ entry }: { entry: VaultEntry }) {
  if (entry.rotationDueInDays === null) {
    return <span className="subtle" style={{ fontSize: 12 }}>No policy</span>;
  }
  const overdue = entry.rotationDueInDays <= 0;
  return (
    <span
      className="row"
      style={{ gap: 5, fontSize: 12, color: overdue ? 'var(--itr-critical)' : entry.rotationDueInDays < 14 ? 'var(--itr-warning)' : 'var(--itr-text-muted)' }}
    >
      <Clock size={12} />
      {overdue ? `${Math.abs(entry.rotationDueInDays)}d overdue` : `in ${entry.rotationDueInDays}d`}
    </span>
  );
}

/* ================================================================== */
/* Detail                                                             */
/* ================================================================== */

export function VaultDetail() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data, loading, error, reload } = useFetch(
    () =>
      api.get<VaultEntry & { resource: Resource | null; accessLog: Array<{ user_email: string; action: string; ip: string; ts: number }> }>(
        `/api/vault/entries/${id}`,
      ),
    [id],
  );

  const remove = async () => {
    if (!confirm(`Delete "${data?.name}"? This cannot be undone.`)) return;
    await api.delete(`/api/vault/entries/${id}`);
    toast('Credential deleted', 'good');
    navigate('/vault');
  };

  if (loading && !data) return <SkeletonTable rows={5} cols={2} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Credential not found" />;

  return (
    <div className="content-narrow" style={{ maxWidth: 780 }}>
      <div className="page-header">
        <div className="grow">
          <div className="row" style={{ gap: 8 }}>
            <Badge>{data.type.replace('_', ' ')}</Badge>
            {data.compromised && <Badge intent="bad">compromised</Badge>}
            {data.rotationDueInDays !== null && data.rotationDueInDays <= 0 && <Badge intent="warn">rotation overdue</Badge>}
          </div>
          <h1 className="page-title" style={{ marginTop: 8 }}>
            {data.name}
          </h1>
          {data.url && (
            <div className="page-subtitle">
              <a href={data.url} target="_blank" rel="noreferrer noopener">
                {data.url}
              </a>
            </div>
          )}
        </div>
        <div className="row">
          {can('vault:write') && (
            <>
              <button className="btn" onClick={() => setEditOpen(true)}>
                Edit
              </button>
              <button className="btn btn-icon" onClick={remove} title="Delete" aria-label="Delete">
                <Trash2 size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body col" style={{ gap: 14 }}>
          {data.username && <CopyRow label="Username" value={data.username} />}
          {data.hasSecret && <SecretRow entryId={data.id} label="Password" field="secret" canReveal={can('vault:reveal')} />}
          {data.hasTotp && <TotpRow entryId={data.id} canReveal={can('vault:reveal')} />}
          {data.hasNotes && <SecretRow entryId={data.id} label="Notes" field="notes" canReveal={can('vault:reveal')} multiline />}

          <div className="row" style={{ gap: 24, flexWrap: 'wrap', paddingTop: 4 }}>
            <div>
              <div className="subtle" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Strength
              </div>
              <div style={{ marginTop: 4 }}>
                <StrengthMeter strength={data.strength} />
              </div>
            </div>
            <div>
              <div className="subtle" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Last rotated
              </div>
              <div style={{ marginTop: 4, fontSize: 13 }}>
                {data.lastRotatedAt ? formatRelative(data.lastRotatedAt) : 'never'}
              </div>
            </div>
            {data.expiresAt && (
              <div>
                <div className="subtle" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Expires
                </div>
                <div style={{ marginTop: 4, fontSize: 13 }}>{new Date(data.expiresAt).toLocaleDateString(getLocale())}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {data.resource && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <span className="card-title">Linked resource</span>
          </div>
          <a
            href={`/resources/${data.resource.id}`}
            className="row"
            style={{ padding: '12px 16px', gap: 10, color: 'inherit', textDecoration: 'none' }}
          >
            <span className="grow" style={{ fontWeight: 550 }}>
              <ReachDot health={data.resource.health} />
              {data.resource.name}
            </span>
            <Badge>{data.resource.kind}</Badge>
          </a>
        </div>
      )}

      {data.accessLog?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Access log</span>
            <span className="card-subtitle" style={{ marginLeft: 'auto' }}>
              every reveal is recorded
            </span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th style={{ width: 120 }}>Action</th>
                  <th style={{ width: 130 }}>From</th>
                  <th data-align="right" style={{ width: 120 }}>
                    When
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.accessLog.map((row, i) => (
                  <tr key={i}>
                    <td>{row.user_email}</td>
                    <td>
                      <Badge intent={row.action.startsWith('reveal') ? 'warn' : 'neutral'}>{row.action}</Badge>
                    </td>
                    <td className="mono subtle">{row.ip ?? '—'}</td>
                    <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
                      {formatRelative(row.ts)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Drawer open={editOpen} onClose={() => setEditOpen(false)} title={`Edit ${data.name}`}>
        <CredentialForm
          entry={data}
          onSaved={() => {
            setEditOpen(false);
            reload();
          }}
        />
      </Drawer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Secret display                                                      */
/* ------------------------------------------------------------------ */

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div>
      <div className="subtle" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
        {label}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <span className="mono grow truncate">{value}</span>
        <button className="btn btn-sm btn-icon" onClick={copy} title="Copy" aria-label={`Copy ${label}`}>
          {copied ? <Check size={13} style={{ color: 'var(--itr-success)' }} /> : <Copy size={13} />}
        </button>
      </div>
    </div>
  );
}

/**
 * Reveals a secret through the audited endpoint.
 *
 * The plaintext is only ever held in component state and is cleared after 30
 * seconds — a credential left visible on an unattended screen is the most common
 * real-world way a vault leaks.
 */
function SecretRow({
  entryId,
  label,
  field,
  canReveal,
  multiline,
}: {
  entryId: string;
  label: string;
  field: 'secret' | 'notes';
  canReveal: boolean;
  multiline?: boolean;
}) {
  const toast = useToast();
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (value === null) return;
    setSecondsLeft(30);
    const tick = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    const hide = setTimeout(() => setValue(null), 30_000);
    return () => {
      clearInterval(tick);
      clearTimeout(hide);
    };
  }, [value]);

  const reveal = async () => {
    setBusy(true);
    try {
      const result = await api.post<{ value: string | null }>(`/api/vault/entries/${entryId}/reveal`, { field });
      setValue(result.value ?? '');
    } catch (err) {
      toast('Could not reveal this', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (value === null) {
      // Copy without displaying: the common case is pasting into a device login,
      // and never rendering the secret is strictly safer.
      const result = await api.post<{ value: string | null }>(`/api/vault/entries/${entryId}/reveal`, { field });
      await navigator.clipboard.writeText(result.value ?? '');
    } else {
      await navigator.clipboard.writeText(value);
    }
    setCopied(true);
    toast('Copied to clipboard — it will not be cleared automatically', 'info');
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div>
      <div className="row" style={{ marginBottom: 5 }}>
        <span className="subtle" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
        {value !== null && (
          <span className="subtle nums" style={{ fontSize: 11, marginLeft: 8 }}>
            hides in {secondsLeft}s
          </span>
        )}
      </div>
      <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
        {multiline ? (
          <div
            className="mono grow"
            style={{
              whiteSpace: 'pre-wrap',
              padding: '8px 10px',
              background: 'var(--itr-surface-sunken)',
              borderRadius: 'var(--itr-radius-sm)',
              minHeight: 34,
            }}
          >
            {value ?? '••••••••••••'}
          </div>
        ) : (
          <span className="mono grow truncate" style={{ letterSpacing: value === null ? '0.15em' : undefined }}>
            {value ?? '••••••••••••'}
          </span>
        )}

        {canReveal && (
          <>
            <button
              className="btn btn-sm btn-icon"
              onClick={() => (value === null ? void reveal() : setValue(null))}
              disabled={busy}
              title={value === null ? 'Reveal' : 'Hide'}
              aria-label={value === null ? 'Reveal' : 'Hide'}
            >
              {value === null ? <Eye size={13} /> : <EyeOff size={13} />}
            </button>
            <button className="btn btn-sm btn-icon" onClick={copy} title="Copy" aria-label="Copy">
              {copied ? <Check size={13} style={{ color: 'var(--itr-success)' }} /> : <Copy size={13} />}
            </button>
          </>
        )}
      </div>
      {!canReveal && (
        <div className="field-help" style={{ marginTop: 5 }}>
          Your role can see that this credential exists, but not its value.
        </div>
      )}
    </div>
  );
}

function TotpRow({ entryId, canReveal }: { entryId: string; canReveal: boolean }) {
  const [code, setCode] = useState<{ code: string; expiresInSec: number } | null>(null);
  const [remaining, setRemaining] = useState(0);

  const fetchCode = async () => {
    const result = await api.post<{ code: string; expiresInSec: number }>(`/api/vault/entries/${entryId}/totp`);
    setCode(result);
    setRemaining(result.expiresInSec);
  };

  useEffect(() => {
    if (!code) return;
    const timer = setInterval(() => {
      setRemaining((r) => {
        // Auto-refresh at the step boundary so the displayed code is never stale.
        if (r <= 1) {
          void fetchCode();
          return 30;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code?.code]);

  if (!canReveal) return null;

  return (
    <div>
      <div className="subtle" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
        One-time code
      </div>
      <div className="row" style={{ gap: 10 }}>
        {code ? (
          <>
            <span className="mono" style={{ fontSize: 20, letterSpacing: '0.14em', fontWeight: 620 }}>
              {code.code.slice(0, 3)} {code.code.slice(3)}
            </span>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                background: `conic-gradient(var(--itr-primary) ${(remaining / 30) * 360}deg, var(--itr-surface-sunken) 0)`,
              }}
              title={`${remaining}s remaining`}
            />
            <button className="btn btn-sm btn-icon" onClick={() => void navigator.clipboard.writeText(code.code)} aria-label="Copy code">
              <Copy size={13} />
            </button>
          </>
        ) : (
          <button className="btn btn-sm" onClick={fetchCode}>
            <Eye size={13} /> Show code
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Form                                                                */
/* ------------------------------------------------------------------ */

function CredentialForm({ entry, onSaved }: { entry?: VaultEntry; onSaved: () => void }) {
  const toast = useToast();
  const { data: resources } = useFetch(() => api.get<Resource[]>('/api/resources', { limit: 300 }), []);
  const [form, setForm] = useState({
    name: entry?.name ?? '',
    type: entry?.type ?? 'password',
    username: entry?.username ?? '',
    secret: '',
    url: entry?.url ?? '',
    notes: '',
    totpSecret: '',
    resourceId: entry?.resourceId ?? '',
    rotationDays: entry?.rotationDays ? String(entry.rotationDays) : '',
    tags: entry?.tags.join(', ') ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [strength, setStrength] = useState<number | null>(null);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // Live strength feedback while typing, scored server-side so the rule is the
  // same one the audit report uses.
  const debouncedSecret = useDebounced(form.secret, 350);
  useEffect(() => {
    if (!debouncedSecret) {
      setStrength(null);
      return;
    }
    void api
      .post<{ strength: number }>('/api/vault/strength', { password: debouncedSecret })
      .then((r) => setStrength(r.strength))
      .catch(() => {});
  }, [debouncedSecret]);

  const generate = async () => {
    const result = await api.post<{ passwords: Array<{ value: string }> }>('/api/vault/generate', { length: 24 });
    const generated = result.passwords[0]?.value ?? '';
    setForm((f) => ({ ...f, secret: generated }));
    setShowSecret(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      type: form.type,
      username: form.username || null,
      url: form.url || null,
      resourceId: form.resourceId || null,
      rotationDays: form.rotationDays ? Number(form.rotationDays) : null,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      // Empty means "leave it alone" on edit, so an accidental blank save never
      // wipes a stored secret.
      ...(form.secret ? { secret: form.secret } : {}),
      ...(form.notes ? { notes: form.notes } : {}),
      ...(form.totpSecret ? { totpSecret: form.totpSecret } : {}),
    };

    try {
      if (entry) await api.patch(`/api/vault/entries/${entry.id}`, payload);
      else await api.post('/api/vault/entries', payload);
      toast(entry ? 'Credential updated' : 'Credential saved', 'good');
      onSaved();
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} placeholder="HQ FortiGate admin" />
      </div>

      <div className="field">
        <label className="field-label">Type</label>
        <select className="select" value={form.type} onChange={set('type')}>
          {['password', 'ssh_key', 'api_key', 'certificate', 'wifi', 'license', 'database', 'note'].map((t) => (
            <option key={t} value={t}>
              {t.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">Username</label>
        <input className="input" value={form.username} onChange={set('username')} autoComplete="off" />
      </div>

      <div className="field">
        <label className="field-label">{entry ? 'New secret (leave blank to keep the current one)' : 'Secret'}</label>
        <div className="row" style={{ gap: 6 }}>
          <input
            className="input grow"
            type={showSecret ? 'text' : 'password'}
            value={form.secret}
            onChange={set('secret')}
            autoComplete="new-password"
            placeholder={entry ? '••••••••' : ''}
          />
          <button type="button" className="btn btn-icon" onClick={() => setShowSecret((v) => !v)} aria-label="Toggle visibility">
            {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
          <button type="button" className="btn btn-icon" onClick={generate} title="Generate a strong password" aria-label="Generate">
            <Sparkles size={15} />
          </button>
        </div>
        {strength !== null && (
          <div style={{ marginTop: 7 }}>
            <StrengthMeter strength={strength} />
          </div>
        )}
      </div>

      <div className="field">
        <label className="field-label">URL</label>
        <input className="input" value={form.url} onChange={set('url')} placeholder="https://10.0.0.1" />
      </div>

      <div className="field">
        <label className="field-label">Linked resource</label>
        <select className="select" value={form.resourceId} onChange={set('resourceId')}>
          <option value="">Not linked</option>
          {(resources ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} ({r.kind})
            </option>
          ))}
        </select>
        <span className="field-help">Linking inherits the resource's customer, so tenant scoping stays correct.</span>
      </div>

      <div className="field">
        <label className="field-label">Rotate every (days)</label>
        <input className="input" type="number" min={1} value={form.rotationDays} onChange={set('rotationDays')} placeholder="90" />
        <span className="field-help">Leave blank for no rotation policy. Overdue credentials appear in the vault audit.</span>
      </div>

      <div className="field">
        <label className="field-label">TOTP secret</label>
        <input className="input" value={form.totpSecret} onChange={set('totpSecret')} placeholder="Base32 shared secret" autoComplete="off" />
      </div>

      <div className="field">
        <label className="field-label">Notes</label>
        <textarea className="textarea" value={form.notes} onChange={set('notes')} placeholder="Encrypted alongside the secret." />
      </div>

      <div className="field">
        <label className="field-label">Tags</label>
        <input className="input" value={form.tags} onChange={set('tags')} placeholder="network, admin, production" />
      </div>

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : entry ? 'Save changes' : 'Save credential'}
      </button>
    </form>
  );
}
